export const LIMITS = {
  photo: { max: 10, bytes: 10 * 1024 * 1024 },
  file: { max: 10, bytes: 20 * 1024 * 1024 },
  audio: { max: 3, bytes: 20 * 1024 * 1024 },
  audioSeconds: 600
};

const FILE_TYPES = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
  csv: 'text/csv'
};

const AUDIO_TYPES = ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/x-m4a', 'audio/aac'];
const AUDIO_ALIASES = { 'audio/mp3': 'audio/mpeg', 'audio/x-wav': 'audio/wav', 'audio/wave': 'audio/wav', 'audio/m4a': 'audio/x-m4a' };
const AUDIO_EXT = { m4a: 'audio/x-m4a', mp3: 'audio/mpeg', wav: 'audio/wav', webm: 'audio/webm', ogg: 'audio/ogg', aac: 'audio/aac', mp4: 'audio/mp4' };

export const FILE_ACCEPT = '.pdf,.doc,.docx,.xlsx,.txt,.csv';
export const AUDIO_ACCEPT = 'audio/*,.m4a,.mp3,.wav,.webm,.ogg,.aac';

export const baseType = (type) => (type || '').split(';')[0].trim().toLowerCase();
const extOf = (name) => (name.split('.').pop() || '').toLowerCase();

export const fileMime = (file) => {
  const byExt = FILE_TYPES[extOf(file.name)];
  const type = baseType(file.type);
  return Object.values(FILE_TYPES).includes(type) ? type : byExt || null;
};

export const audioMime = (file) => {
  const type = AUDIO_ALIASES[baseType(file.type)] || baseType(file.type);
  if (AUDIO_TYPES.includes(type)) return type;
  return AUDIO_EXT[extOf(file.name)] || null;
};

export const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatDuration = (s) => {
  if (s == null) return '';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s % 60)).padStart(2, '0')}`;
};

// Resizes to a 2048px long edge and re-encodes as JPEG so uploads stay small.
export const compressImage = async (file) => {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error(`${file.name}: this image format is not supported. Try JPEG, PNG or WebP.`));
      el.src = url;
    });
    const scale = Math.min(1, 2048 / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) throw new Error(`${file.name}: could not process this image.`);
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const readAudioDuration = (blob) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const el = new Audio();
    const done = (value) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    el.preload = 'metadata';
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? Math.round(el.duration) : null);
    el.onerror = () => done(null);
    el.src = url;
  });